import * as React from "react";
import { useBodyScrollController } from "../providers";

export interface DrawerProps {
  guid: string;

  present?: boolean;
  handleClose?: () => void;
  className?: string;

  showBackButton?: boolean;
  handleBack?: () => void;

  dontShowOverlay?: boolean;
}

/**
 * Can't use the browser's built-in requestAnimationFrame because it's not supported fully.
 */
const oneFrameOfAnimation = 8;

export const Drawer: React.FunctionComponent<DrawerProps> = (props) => {
  const thisEventNameBase = `bbnt-common-drawer-${props.guid}`;

  const { acquireBodyScrollLock, releaseBodyScrollLock } =
    useBodyScrollController();

  const [isPresentingInAnyWay, setIsPresentingInAnyWay] =
    React.useState<boolean>(false);
  const [presentFirstLayer, setPresentFirstLayer] =
    React.useState<boolean>(false);
  const [
    isWaitingForTheCloseAnimationFirstLayer,
    setIsWaitingForTheCloseAnimationFirstLayer,
  ] = React.useState<boolean>(false);
  const [presentSecondLayer, setPresentSecondLayer] =
    React.useState<boolean>(false);

  const modalRef = React.useRef<HTMLDivElement>(null);

  const handleEventDrawerControl_Open = React.useCallback(() => {
    setIsPresentingInAnyWay(true);
    setPresentSecondLayer(false);
    window.setTimeout(() => {
      setPresentFirstLayer(true);
    }, oneFrameOfAnimation);
  }, [setIsPresentingInAnyWay, setPresentSecondLayer, setPresentFirstLayer]);
  const handleEventDrawerControl_HasRenderedWhileOpen =
    React.useCallback(() => {
      if (!presentSecondLayer) {
        setIsPresentingInAnyWay(true);
        setPresentSecondLayer(true);

        acquireBodyScrollLock(thisEventNameBase);
        modalRef.current?.focus();
      }
    }, [
      presentSecondLayer,
      setIsPresentingInAnyWay,
      setPresentSecondLayer,
      acquireBodyScrollLock,
    ]);
  const handleEventDrawerControl_Close = React.useCallback(() => {
    // animation
    setPresentSecondLayer(false);
    window.setTimeout(() => {
      setPresentFirstLayer(false);
    }, 299);

    // modal
    releaseBodyScrollLock(thisEventNameBase);
  }, [setPresentFirstLayer, setPresentSecondLayer, releaseBodyScrollLock]);
  const handleEventDrawerControl_ClosedAlready = React.useCallback(() => {
    window.setTimeout(() => {
      setIsWaitingForTheCloseAnimationFirstLayer(false);
    }, 300);
  }, [setIsWaitingForTheCloseAnimationFirstLayer]);
  const handleEventDrawerControl_CompletelyClosed = React.useCallback(() => {
    setIsPresentingInAnyWay(false);
  }, [setIsPresentingInAnyWay]);

  /**
   * Event Listening
   */

  React.useEffect(() => {
    window.addEventListener(
      `${thisEventNameBase}-open`,
      handleEventDrawerControl_Open
    );
    return () => {
      window.removeEventListener(
        `${thisEventNameBase}-open`,
        handleEventDrawerControl_Open
      );
    };
  }, [handleEventDrawerControl_Open]);
  React.useEffect(() => {
    window.addEventListener(
      `${thisEventNameBase}-has-rendered-while-open`,
      handleEventDrawerControl_HasRenderedWhileOpen
    );
    return () => {
      window.removeEventListener(
        `${thisEventNameBase}-has-rendered-while-open`,
        handleEventDrawerControl_HasRenderedWhileOpen
      );
    };
  }, [handleEventDrawerControl_HasRenderedWhileOpen]);
  React.useEffect(() => {
    window.addEventListener(
      `${thisEventNameBase}-close`,
      handleEventDrawerControl_Close
    );
    return () => {
      window.removeEventListener(
        `${thisEventNameBase}-close`,
        handleEventDrawerControl_Close
      );
    };
  }, [handleEventDrawerControl_Close]);
  React.useEffect(() => {
    window.addEventListener(
      `${thisEventNameBase}-closed-already`,
      handleEventDrawerControl_ClosedAlready
    );
    return () => {
      window.removeEventListener(
        `${thisEventNameBase}-closed-already`,
        handleEventDrawerControl_ClosedAlready
      );
    };
  }, [handleEventDrawerControl_ClosedAlready]);
  React.useEffect(() => {
    window.addEventListener(
      `${thisEventNameBase}-completely-closed`,
      handleEventDrawerControl_CompletelyClosed
    );
    return () => {
      window.removeEventListener(
        `${thisEventNameBase}-completely-closed`,
        handleEventDrawerControl_CompletelyClosed
      );
    };
  }, [handleEventDrawerControl_CompletelyClosed]);

  /**
   * Event Triggering
   */

  React.useEffect(() => {
    if (!props.present && presentFirstLayer) {
      window.dispatchEvent(new Event(`${thisEventNameBase}-close`));
    } else if (props.present && presentFirstLayer) {
      window.dispatchEvent(
        new Event(`${thisEventNameBase}-has-rendered-while-open`)
      );
    } else if (props.present && !presentFirstLayer) {
      window.dispatchEvent(new Event(`${thisEventNameBase}-open`));
    } else if (!props.present && !presentFirstLayer) {
      window.dispatchEvent(new Event(`${thisEventNameBase}-closed-already`));
    }
  }, [props.present, presentFirstLayer]);
  React.useEffect(() => {
    if (
      !props.present &&
      !presentFirstLayer &&
      !isWaitingForTheCloseAnimationFirstLayer
    ) {
      window.dispatchEvent(new Event(`${thisEventNameBase}-completely-closed`));
    }
  }, [
    props.present,
    presentFirstLayer,
    isWaitingForTheCloseAnimationFirstLayer,
  ]);

  /**
   * Rendering
   */

  if (!isPresentingInAnyWay) {
    return null;
  }

  return (
    <aside
      className={`bbnt-react-drawer ${props.className || ""} ${
        presentFirstLayer ? "show" : "hide"
      }`}
      ref={modalRef}
    >
      {!props.dontShowOverlay && (
        <div
          className={`overlay ${presentSecondLayer ? "show" : "hide"}`}
          onClick={props.handleClose}
        ></div>
      )}
      <div
        className={`modal-content-container ${
          presentSecondLayer ? "show" : "hide"
        }`}
      >
        <div data-role="focusable-start" tabIndex={0}></div>
        <div className="modal-inner-wrap">
          <div className="modal-level-actions-container">
            {props.showBackButton && (
              <button
                className="back-button"
                onClick={props.handleBack}
              ></button>
            )}
            <button
              className="close-button"
              onClick={props.handleClose}
            ></button>
          </div>
          <div className="modal-content">{props.children || []}</div>
        </div>
        <div data-role="focusable-end" tabIndex={0}></div>
      </div>
    </aside>
  );
};
